import React, { useMemo, useState } from "react";
import { compose, withHandlers, withPropsOnChange } from "recompose";
import { range } from "lodash";
import { LocalForm, Control, actions } from "react-redux-form";
import {
  Page,
  onMount,
  FormGroup,
  DatePicker,
  SubmitButton,
} from "components/common";
import Select from "react-select";
import { withOrganisationContextConsumer } from "contexts/OrganisationContext";
import { getMonthEnd, getMonthStart } from "./util";
import { ReportContainer } from "./components";
import { useTranslation } from "react-i18next";
import { Col, Row } from "reactstrap";
import { withDefaultReportParams } from "./hooks/useDefaultReportParams";

const trackingCategories = [];

export const ProfitAndLoss = ({
  data,
  item: itemProp,
  handleSubmit,
  setState,
  timeframes,
  periods,
  settings,
  organisation,
  dispatch,
}) => {
  const { t } = useTranslation();
  const [item, setItem] = useState(itemProp);

  const compareOptions = useMemo(
    () => [
      {
        label: t("Compare Periods"),
        value: "periods",
      },
      ...trackingCategories.map((i) => ({
        value: i.TrackingCategoryID,
        label: t("Compare {{ Name }}", i),
      })),
    ],
    [t, trackingCategories]
  );

  const { compare } = item;

  return (
    <Page title={data.ReportTitles[0]} printTitle={false} translate={false}>
      <LocalForm
        className="d-print-none"
        getDispatch={(dispatch) => setState({ dispatch })}
        onSubmit={handleSubmit}
        onChange={setItem}
        initialState={item}
      >
        <Row form>
          <Col lg="auto">
            <FormGroup label="From date">
              <DatePicker model=".fromDate" className="form-control" />
            </FormGroup>
          </Col>
          <Col lg="auto">
            <FormGroup label="To date">
              <DatePicker
                style={{ width: "12rem" }}
                model=".toDate"
                className="form-control"
              />
            </FormGroup>
          </Col>
          {trackingCategories.length ? (
            <Col lg="auto">
              <FormGroup label="Compare" addControlClass={false}>
                <Control.select
                  component={Select}
                  style={{ minWidth: "13rem" }}
                  options={compareOptions}
                  searchable={false}
                  simpleValue
                  clearable={false}
                  model=".compare"
                />
              </FormGroup>
            </Col>
          ) : null}
          {compare === "periods" && (
            <>
              <Col lg="auto">
                <FormGroup label="Periods" addControlClass={false}>
                  <Control.select
                    component={Select}
                    simpleValue
                    style={{ minWidth: "10rem" }}
                    options={periods}
                    //clearable={false}
                    searchable={false}
                    model=".periods"
                    placeholder={t('Select...')}
                  />
                </FormGroup>
              </Col>
              <Col lg="auto">
                <FormGroup label="Timeframe" addControlClass={false}>
                  <Control.select
                    component={Select}
                    simpleValue
                    clearable={false}
                    style={{ minWidth: "10rem" }}
                    options={timeframes}
                    searchable={false}
                    model=".timeframe"
                    placeholder={t('Select...')}
                  />
                </FormGroup>
              </Col>
            </>
          )}
          <Col lg="auto">
            <FormGroup label="&nbsp;" className="mr-3">
              <FormGroup label="Cash transactions only" check>
                <Control.checkbox model=".paymentsOnly" />
              </FormGroup>
            </FormGroup>
          </Col>
          <Col lg="auto">
            <FormGroup label="&nbsp;">
              <SubmitButton size="sm" className="d-block">
                Update
              </SubmitButton>
            </FormGroup>
          </Col>
        </Row>
      </LocalForm>
      <ReportContainer
        report={data}
      />
    </Page>
  );
};

const loadReport = (item, xero) =>
  xero.get("reports/ProfitAndLoss", {
    params: {
      extra_params: item,
    },
  }).get("data");

export const enhance = compose(
  withOrganisationContextConsumer,
  withDefaultReportParams(() => ({
    fromDate: getMonthStart(),
    toDate: getMonthEnd(),
    //periods: 1,
    compare: "periods",
    timeframe: "MONTH",
  })),
  onMount(({ xero, fetchSettings, fetchOrganisation, item }) => () => {
    return Promise.props({
      data: loadReport(
        {
          ...item,
          tracking_category:
            item.compare !== "periods" ? item.compare : undefined,
        },
        xero
      ),
      settings: fetchSettings(),
      organisation: fetchOrganisation(),
      item,
    });
  }),
  withPropsOnChange(["t"], ({ t }) => ({
    periods: range(1, 11 + 1).map((i) => ({ label: i, value: i })),
    timeframes: [
      ["MONTH", t("Month")],
      ["QUARTER", t("Quarter")],
      ["YEAR", t("Year")],
    ].map(([value, label]) => ({ label, value })),
  })),
  withHandlers({
    handleSubmit:
      ({ setState, xero, dispatch, setReportParams }) =>
      (item) => {
        setReportParams(item);
        return loadReport(
          {
            ...item,
            tracking_category:
              item.compare !== "periods" ? item.compare : undefined,
          },
          xero
        )
          .then((data) => setState({ data }))
          .finally(() => dispatch(actions.setSubmitted("local", true)));
      },
  })
);

export default enhance(ProfitAndLoss);
